import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Header,
  Heading,
  Text,
  ResponsiveContext,
  Layer,
  Select,
} from "grommet";
import { AuthContext } from "../../contexts/AuthContext"; // Import AuthContext
import { useNavigate } from "react-router-dom";
import axios from "axios"; // Import mock tokens
import { jwtDecode } from "jwt-decode"; // Import jwt-decode
import API_URL from "../../config/config";
import { useSearchParams } from "react-router-dom";
import { set } from "mongoose";
import { Info } from "grommet-icons";

export const LoginForm = ({ setLoading }) => {
  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const size = React.useContext(ResponsiveContext);
  const { login } = useContext(AuthContext);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [userId, setUserId] = useState("");
  const [userTableId, setUserTableId] = useState("");
  const [openRedirectModal, setOpenRedirectModal] = useState(false);

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      // After the first request is done, run the second request to /user/redirect
      const redirectResponse = await axios.get(`${API_URL}/user/redirect`);

      // Extract the JWT token and the URL from the response
      const { jwtToken, url } = redirectResponse.data;

      // Perform the login operation with the JWT token
      login(jwtToken);
      const decodedToken = jwtDecode(jwtToken);
      const userId = decodedToken.user_id;
      const userTableId = decodedToken.user_table_id;
      setUserTableId(userTableId);
      setUserId(userId);
      const userRole = decodedToken.role;
      console.log(decodedToken);

      setRedirectUrl(url);
      if (url === "master-access") {
        setOpenRedirectModal(true);
        console.log("Opening modal...");
        setLoading(false);
      } else {
        navigate(url);
      }
    } catch (error) {
      console.error("Error redirecting:", error);
    }
  };

  // Send a request to the OKTA backend to check the auth status
  const oktaCallback = async () => {
    try {
      const status = await axios.get(
        `${API_URL}/auth/callback?code=${code}&state=${state}`,
      );
      console.log("Auth status:", status);
      if (status.data.isAuthenticated) {
        fetchData();
        return true;
      }
    } catch (error) {
      console.error("Error fetching auth status:", error);
    }
    return false;
  };

  const getAuthStatus = async () => {
    try {
      const status = await axios.get(`${API_URL}/user/status`);
      console.log("Auth status:", status);
      if (status.data.isAuthenticated) {
        fetchData();

        return true;
      }
    } catch (error) {
      console.error("Error fetching auth status:", error);
    }
    return false;
  };

  useEffect(() => {
    const checkAuth = async () => {
      const callbackAuth = await oktaCallback();
      const statusAuth = await getAuthStatus();
      if (!callbackAuth && !statusAuth) {
        setLoading(false);
      }
    };
    checkAuth();
  }, [setLoading]);

  const handleLogin = () => {
    window.location.href = `${API_URL}/auth/okta/login`; // Redirect to backend login route
  };

  const handleRedirectChoice = (role) => {
    setOpenRedirectModal(false);
    if (role === "Pursuit Access") {
      navigate("/pursuit/dashboard"); // Pursuit Access dashboard route
    } else if (role === "Portfolio Management") {
      navigate("/admin/dashboard"); // Admin dashboard route
    } else if (role === "Manager") {
      navigate(`/${userId}/manager/dashboard`); // Manager dashboard route
    } else if (role === "User") {
      navigate(`/add-proficiency/${userTableId}/`); // User dashboard route
    }
  };

  return (
    <Box width="medium">
      <Header
        direction="column"
        align="start"
        gap="xxsmall"
        pad={{ horizontal: "xxsmall" }}
      >
        <Heading level={2} margin="none">
          Sign In or Sign Up
        </Heading>
        <Text>for your HPE Digital Skill Advisor account</Text>
      </Header>
      <Box pad={{ horizontal: "xxsmall" }}>
        <Box
          align={!["xsmall", "small"].includes(size) ? "start" : undefined}
          margin={{ top: "small", bottom: "small" }}
        >
          <Button
            label="Click here"
            primary
            type="submit"
            onClick={handleLogin}
            margin={{ bottom: "small" }}
          />
        </Box>
      </Box>

      {/* Full-screen Redirect Modal */}
      {openRedirectModal && (
        <Layer
          align="center"
          justify="center"
          position="center"
          onEsc={() => setOpenRedirectModal(false)}
          onClickOutside={() => setOpenRedirectModal(false)}
        >
          <Box
            align="center"
            justify="center"
            pad="medium"
            background="light-1" // Add a background color for the modal
            round="small" // Rounded corners for the modal
            elevation="large" // Add shadow to the modal for a pop effect
            width="large" // Set a maximum width for the modal
            style={{ textAlign: "center" }} // Center-align text
          >
            <Heading level={2} margin="none" color="dark-1">
              Which portal would you like to access?
            </Heading>
            <Box
              pad="medium"
              background="light-2"
              round="small"
              flex={{ grow: 1, shrink: 1 }} // Flexible width
              border={{ color: "brand", size: "small" }}
              width="full"
              margin={{ top: "small" }} // Add margin to the top
            >
              <Box direction="row" align="center" gap="small">
                <Info size="large" color="brand" />

                <Text size="medium" color="dark-3">
                  You have been granted Pursuit access - select 'Pursuit Access'
                  to go to the Pursuit dashboard. To reach the Portfolio portal,
                  choose 'Portfolio Management'. Alternatively, you may log in
                  to the Manager or User portals, depending on the other roles
                  assigned to you.
                </Text>
              </Box>

              {/* Body text */}
            </Box>

            <Box
              direction="row"
              gap="medium"
              margin={{ top: "medium" }}
              align="center"
              justify="center"
              width="medium"
            >
              <Select
                options={
                  userId && userTableId
                    ? ["Pursuit Access", "Manager", "User", "Portfolio Management"]
                    : ["Pursuit Access", "User", "Portfolio Management"]
                }
                value={""}
                onChange={({ option }) => handleRedirectChoice(option)}
                placeholder="Select Portal"
                size="medium"
                margin={{ top: "small" }}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};
